"use client";

import { useEffect, useRef } from "react";
import { useSession } from "next-auth/react";
import { usePathname, useSearchParams } from "next/navigation";
import {
  applyAnalyticsContext,
  getAnalyticsPageContext,
  hasTrackingConsent,
  setUserId,
  setUserProperties,
  trackPageView,
} from "@/lib/analytics";

function getSearchValue(searchParams) {
  const value = searchParams?.toString() || "";
  return value ? `?${value}` : "";
}

export default function AnalyticsTracker() {
  const { data: session } = useSession();
  const pathname = usePathname() || "/";
  const searchParams = useSearchParams();
  const lastTrackedLocationRef = useRef("");

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const context = getAnalyticsPageContext({
      pathname,
      search: getSearchValue(searchParams),
      session,
      hostname: window.location.hostname,
      origin: window.location.origin,
      referrer: lastTrackedLocationRef.current || document.referrer || "",
    });

    applyAnalyticsContext(context);

    if (!hasTrackingConsent() || !context.shouldTrack) {
      return;
    }

    const syncUserState = () => {
      if (!session?.user) {
        return;
      }

      setUserId(session.user.id);
      setUserProperties({
        user_type: session.user.role || "user",
        workspace_id: session.user.workspaceId || "none",
        workspace_name:
          session.workspace?.name || session.user.workspaceName || "none",
        email_domain: session.user.email?.split("@")?.[1] || "unknown",
      });
    };

    const flushPageView = () => {
      if (typeof window.gtag !== "function") {
        return false;
      }

      if (lastTrackedLocationRef.current === context.pageLocation) {
        syncUserState();
        return true;
      }

      trackPageView({
        path: context.pagePath,
        location: context.pageLocation,
        referrer: context.pageReferrer,
        title: document.title,
        section: context.appSection,
        trafficType: context.trafficType,
      });
      syncUserState();
      lastTrackedLocationRef.current = context.pageLocation;
      return true;
    };

    if (flushPageView()) {
      return;
    }

    const handleGaReady = () => {
      flushPageView();
    };
    const retryId = window.setInterval(() => {
      if (flushPageView()) {
        window.clearInterval(retryId);
      }
    }, 250);

    window.addEventListener("ga4-ready", handleGaReady);

    return () => {
      window.removeEventListener("ga4-ready", handleGaReady);
      window.clearInterval(retryId);
    };
  }, [
    pathname,
    searchParams,
    session,
  ]);

  return null;
}
